import { useState, useEffect, useEffectEvent, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { useLocalStorage } from './useLocalStorage'
import { tablePreferencesService } from '@/services/tablePreferencesService'
import type { VisibilityState, ColumnSizingState, OnChangeFn } from '@tanstack/react-table'

// Query keys para preferencias de tablas
export const tablePreferencesKeys = {
  all: ['table-preferences'] as const,
  table: (tableKey: string, organizacionId?: string) =>
    [...tablePreferencesKeys.all, tableKey, organizacionId] as const,
}

export interface UseTablePreferencesOptions {
  /** Persistir en Supabase además de localStorage */
  persistInSupabase?: boolean
  /** ID de la organización para preferencias específicas */
  organizacionId?: string
  /** Visibilidad por defecto de las columnas */
  defaultVisibility?: VisibilityState
  /** Anchos por defecto de las columnas */
  defaultColumnSizing?: ColumnSizingState
  /** Tiempo de debounce para guardar en Supabase (ms) */
  debounceMs?: number
}

export interface UseTablePreferencesReturn {
  /** Estado actual de visibilidad de columnas */
  visibility: VisibilityState
  /** Función para actualizar visibilidad (compatible con TanStack Table) */
  saveVisibility: OnChangeFn<VisibilityState>
  /** Estado actual de anchos de columnas */
  columnSizing: ColumnSizingState
  /** Función para actualizar anchos (compatible con TanStack Table) */
  saveColumnSizing: OnChangeFn<ColumnSizingState>
  /** Restaurar visibilidad a valores por defecto */
  resetVisibility: () => void
  /** Restaurar anchos a valores por defecto */
  resetColumnSizing: () => void
  /** Restaurar todas las preferencias a valores por defecto */
  resetAll: () => void
  /** Indica si está cargando preferencias desde Supabase */
  isLoading: boolean
  /** Indica si está sincronizando con Supabase */
  isSyncing: boolean
}

/**
 * Hook para manejar preferencias de tablas (visibilidad y anchos de columnas)
 *
 * - Guarda en localStorage inmediatamente (funciona offline)
 * - Sincroniza con Supabase cuando hay usuario autenticado
 * - Supabase es fuente de verdad cuando hay datos remotos
 *
 * @example
 * const { visibility, saveVisibility, columnSizing, saveColumnSizing, resetAll } = useTablePreferences('servicios', {
 *   defaultVisibility: { nombre: true, precio: true, estado: true },
 *   defaultColumnSizing: { nombre: 200, precio: 100 },
 * })
 *
 * const table = useReactTable({
 *   state: {
 *     columnVisibility: visibility,
 *     columnSizing,
 *   },
 *   onColumnVisibilityChange: saveVisibility,
 *   onColumnSizingChange: saveColumnSizing,
 *   enableColumnResizing: true,
 *   columnResizeMode: 'onChange',
 *   // ...
 * })
 */
export function useTablePreferences(
  tableKey: string,
  options: UseTablePreferencesOptions = {}
): UseTablePreferencesReturn {
  const {
    persistInSupabase = true,
    organizacionId,
    defaultVisibility = {},
    defaultColumnSizing = {},
    debounceMs = 1000,
  } = options

  const { isAuthenticated, user } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  // Clave completa incluyendo organización si aplica
  const fullKey = organizacionId ? `${tableKey}:${organizacionId}` : tableKey
  const localStorageKeyVisibility = `table:${fullKey}:visibility`
  const localStorageKeySizing = `table:${fullKey}:columnSizing`

  // Estado local desde localStorage - Visibilidad
  const [localVisibility, setLocalVisibility] = useLocalStorage<VisibilityState>(
    localStorageKeyVisibility,
    defaultVisibility
  )

  // Estado local desde localStorage - Column Sizing
  const [localColumnSizing, setLocalColumnSizing] = useLocalStorage<ColumnSizingState>(
    localStorageKeySizing,
    defaultColumnSizing
  )

  // Flag para saber si ya sincronizamos con datos remotos
  const [hasSyncedFromRemote, setHasSyncedFromRemote] = useState(false)

  // Refs para debounce (separados para visibility y sizing)
  const debounceVisibilityRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceSizingRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Obtener userId de Auth0
  const userId = user?.sub

  // Query a Supabase (si hay usuario y está habilitado)
  const {
    data: remotePrefs,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: tablePreferencesKeys.table(fullKey, organizacionId),
    queryFn: () => {
      if (!userId) throw new Error('User ID not available')
      return tablePreferencesService.get(supabase, userId, tableKey, organizacionId)
    },
    enabled: isAuthenticated && persistInSupabase && !!userId && !!supabase,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  })

  // Mutation para guardar en Supabase (ahora acepta visibility y columnSizing)
  const { mutate: saveToSupabase, isPending: isSyncing } = useMutation({
    mutationFn: (prefs: { visibility?: VisibilityState; columnSizing?: ColumnSizingState }) => {
      if (!userId) throw new Error('User ID not available')
      return tablePreferencesService.upsert(supabase, userId, tableKey, prefs, organizacionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tablePreferencesKeys.table(fullKey, organizacionId),
      })
    },
    onError: (error) => {
      console.error('Error sincronizando preferencias con Supabase:', error)
    },
  })

  // Sync: Supabase → localStorage cuando llegan datos remotos (una sola vez)
  const syncFromRemote = useEffectEvent(() => {
    if (isFetched && !hasSyncedFromRemote && remotePrefs) {
      // Sincronizar visibility (si existe en remoto, aunque esté vacío = reset)
      if (remotePrefs.visibility && Object.keys(remotePrefs.visibility).length > 0) {
        setLocalVisibility(remotePrefs.visibility)
      }
      // Sincronizar columnSizing (incluyendo objeto vacío para propagar resets)
      // Un columnSizing vacío {} significa que el usuario reseteó los anchos
      if (remotePrefs.columnSizing !== undefined && remotePrefs.columnSizing !== null) {
        setLocalColumnSizing(remotePrefs.columnSizing)
      }
      setHasSyncedFromRemote(true)
    }
  })
  useEffect(() => {
    syncFromRemote()
  }, [isFetched, remotePrefs, hasSyncedFromRemote])

  // Si no hay datos remotos pero hay locales, hacer upsert inicial
  const upsertLocalToRemote = useEffectEvent(() => {
    if (
      isFetched &&
      isAuthenticated &&
      persistInSupabase &&
      !remotePrefs &&
      !hasSyncedFromRemote
    ) {
      const hasLocalVisibility = Object.keys(localVisibility).length > 0
      const hasLocalSizing = Object.keys(localColumnSizing).length > 0

      if (hasLocalVisibility || hasLocalSizing) {
        // Subir preferencias locales a Supabase
        saveToSupabase({
          visibility: localVisibility,
          columnSizing: localColumnSizing,
        })
        setHasSyncedFromRemote(true)
      }
    }
  })
  useEffect(() => {
    upsertLocalToRemote()
  }, [
    isFetched,
    isAuthenticated,
    persistInSupabase,
    remotePrefs,
    localVisibility,
    localColumnSizing,
    hasSyncedFromRemote,
  ])

  // Función debounced para guardar visibility en Supabase
  const debouncedSaveVisibility = useCallback(
    (newVisibility: VisibilityState) => {
      if (debounceVisibilityRef.current) {
        clearTimeout(debounceVisibilityRef.current)
      }

      debounceVisibilityRef.current = setTimeout(() => {
        if (isAuthenticated && persistInSupabase) {
          saveToSupabase({ visibility: newVisibility })
        }
      }, debounceMs)
    },
    [isAuthenticated, persistInSupabase, saveToSupabase, debounceMs]
  )

  // Función debounced para guardar columnSizing en Supabase
  const debouncedSaveColumnSizing = useCallback(
    (newSizing: ColumnSizingState) => {
      if (debounceSizingRef.current) {
        clearTimeout(debounceSizingRef.current)
      }

      debounceSizingRef.current = setTimeout(() => {
        if (isAuthenticated && persistInSupabase) {
          saveToSupabase({ columnSizing: newSizing })
        }
      }, debounceMs)
    },
    [isAuthenticated, persistInSupabase, saveToSupabase, debounceMs]
  )

  // Cleanup de timers en unmount
  useEffect(() => {
    return () => {
      if (debounceVisibilityRef.current) {
        clearTimeout(debounceVisibilityRef.current)
      }
      if (debounceSizingRef.current) {
        clearTimeout(debounceSizingRef.current)
      }
    }
  }, [])

  // Handler compatible con TanStack Table onColumnVisibilityChange
  const saveVisibility: OnChangeFn<VisibilityState> = useCallback(
    (updaterOrValue) => {
      const newValue =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(localVisibility)
          : updaterOrValue

      // Guardar en localStorage inmediatamente
      setLocalVisibility(newValue)

      // Guardar en Supabase con debounce
      debouncedSaveVisibility(newValue)
    },
    [localVisibility, setLocalVisibility, debouncedSaveVisibility]
  )

  // Handler compatible con TanStack Table onColumnSizingChange
  const saveColumnSizing: OnChangeFn<ColumnSizingState> = useCallback(
    (updaterOrValue) => {
      const newValue =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(localColumnSizing)
          : updaterOrValue

      // Guardar en localStorage inmediatamente
      setLocalColumnSizing(newValue)

      // Guardar en Supabase con debounce
      debouncedSaveColumnSizing(newValue)
    },
    [localColumnSizing, setLocalColumnSizing, debouncedSaveColumnSizing]
  )

  // Restaurar visibility a valores por defecto
  const resetVisibility = useCallback(() => {
    setLocalVisibility(defaultVisibility)

    if (isAuthenticated && persistInSupabase) {
      saveToSupabase({ visibility: defaultVisibility })
    }
  }, [defaultVisibility, setLocalVisibility, isAuthenticated, persistInSupabase, saveToSupabase])

  // Restaurar columnSizing a valores por defecto
  const resetColumnSizing = useCallback(() => {
    setLocalColumnSizing(defaultColumnSizing)

    if (isAuthenticated && persistInSupabase) {
      saveToSupabase({ columnSizing: defaultColumnSizing })
    }
  }, [defaultColumnSizing, setLocalColumnSizing, isAuthenticated, persistInSupabase, saveToSupabase])

  // Restaurar todas las preferencias a valores por defecto
  const resetAll = useCallback(() => {
    setLocalVisibility(defaultVisibility)
    setLocalColumnSizing(defaultColumnSizing)

    if (isAuthenticated && persistInSupabase) {
      saveToSupabase({
        visibility: defaultVisibility,
        columnSizing: defaultColumnSizing,
      })
    }
  }, [
    defaultVisibility,
    defaultColumnSizing,
    setLocalVisibility,
    setLocalColumnSizing,
    isAuthenticated,
    persistInSupabase,
    saveToSupabase,
  ])

  return {
    visibility: localVisibility,
    saveVisibility,
    columnSizing: localColumnSizing,
    saveColumnSizing,
    resetVisibility,
    resetColumnSizing,
    resetAll,
    isLoading,
    isSyncing,
  }
}
